import React, { useEffect, useState } from 'react';
import { Alert, Empty, InputNumber, Modal, Select, Space, message } from 'antd';
import { ConsignmentAddStockBulkPayload, ConsignmentAddStockBulkResult, ConsignmentItem } from '../../model/consignment';
import { api_product } from '../../api/product';

const genIdempotencyKey = (): string => {
    // crypto.randomUUID is available in all modern browsers; fall back for
    // older test/JSDOM environments that don't implement it.
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `add-stock-bulk-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

/** Everything the modal needs to know about the just-opened new Period --
 * built by the parent right after CLOSE_PERIOD succeeds. `newPeriod.id` is
 * what becomes `expected_period_id` on save; it must be the Period the
 * caller just observed as OPEN, never assumed/re-derived later. */
export interface NewPeriodAddStockContext {
    consignmentId: number;
    documentNo: string;
    vendorName?: string;
    newPeriod: { id: number; period_no: number };
    /** CARRY_FORWARD Batches already persisted into `newPeriod` by
     * CLOSE_PERIOD -- read-only context, never resubmitted. */
    carryForwardItems: ConsignmentItem[];
}

interface Props {
    open: boolean;
    context: NewPeriodAddStockContext | null;
    onClose: () => void;
    onSaveBulk: (payload: ConsignmentAddStockBulkPayload) => Promise<ConsignmentAddStockBulkResult>;
}

interface BulkRow {
    product_id: number;
    product_name?: string;
    product_code?: string;
    product_unit_name?: string;
    /** Sum of Carry Forward quantity already in the new Period for this
     * Product -- display only, never part of the submitted add_quantity. */
    carryTotal: number;
    carryBatches: ConsignmentItem[];
    /** undefined = no new deposit for this Product this time (never
     * submitted as a line, never treated as 0). */
    addQuantity: number | undefined;
    sellingPrice: number | undefined;
    ownerCost: number | undefined;
    /** true = added via "+ เพิ่มสินค้าอื่น" (no Carry Forward row backing it). */
    isExtra: boolean;
}

const buildRowsFromCarryForward = (items: ConsignmentItem[]): BulkRow[] => {
    const byProduct = new Map<number, ConsignmentItem[]>();
    items.forEach(it => {
        const list = byProduct.get(it.product_id) || [];
        list.push(it);
        byProduct.set(it.product_id, list);
    });
    return Array.from(byProduct.entries()).map(([product_id, batches]) => {
        const first = batches[0];
        return {
            product_id,
            product_name: first?.product_name,
            product_code: first?.product_code,
            product_unit_name: first?.product_unit_name,
            carryTotal: batches.reduce((sum, b) => sum + Number(b.quantity_received || 0), 0),
            carryBatches: batches,
            addQuantity: undefined,
            sellingPrice: first ? Number(first.selling_price_per_unit || 0) : undefined,
            ownerCost: first ? Number(first.owner_cost_per_unit || 0) : undefined,
            isExtra: false
        };
    });
};

export const ConsignmentNewPeriodAddStockModal: React.FC<Props> = ({ open, context, onClose, onSaveBulk }) => {
    const [rows, setRows] = useState<BulkRow[]>([]);
    const [products, setProducts] = useState<any[]>([]);
    const [extraProductId, setExtraProductId] = useState<number | undefined>(undefined);
    const [idempotencyKey, setIdempotencyKey] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open || !context) return;
        setRows(buildRowsFromCarryForward(context.carryForwardItems));
        setExtraProductId(undefined);
        setIdempotencyKey(genIdempotencyKey());
        api_product('LIST', { page: 1, page_size: 500 })
            .then(res => setProducts(Array.isArray(res) ? res : (res?.items || [])))
            .catch(() => setProducts([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, context?.consignmentId, context?.newPeriod?.id]);

    if (!context) return null;

    const handleQuantityChange = (productId: number, value: number | undefined) => {
        setRows(prev => prev.map(r => r.product_id === productId ? { ...r, addQuantity: value } : r));
    };
    const handlePriceChange = (productId: number, field: 'sellingPrice' | 'ownerCost', value: number | undefined) => {
        setRows(prev => prev.map(r => r.product_id === productId ? { ...r, [field]: value } : r));
    };

    const handleAddExtraProduct = (productId: number) => {
        if (rows.some(r => r.product_id === productId)) {
            message.warning('สินค้านี้อยู่ในรายการอยู่แล้ว กรุณากรอกจำนวนในแถวเดิม');
            setExtraProductId(undefined);
            return;
        }
        const prod = products.find(p => p.id === productId);
        setRows(prev => ([
            ...prev,
            {
                product_id: productId,
                product_name: prod?.product_name,
                product_code: prod?.product_code,
                product_unit_name: prod?.unit_name || prod?.product_unit_name || 'ชิ้น',
                carryTotal: 0,
                carryBatches: [],
                addQuantity: undefined,
                sellingPrice: Number(prod?.price_retail || 0),
                ownerCost: 0,
                isExtra: true
            }
        ]));
        setExtraProductId(undefined);
    };

    const handleRemoveExtraRow = (productId: number) => {
        setRows(prev => prev.filter(r => !(r.isExtra && r.product_id === productId)));
    };

    const handleSaveAll = async () => {
        const items = rows.filter(r => r.addQuantity !== undefined && r.addQuantity !== null && r.addQuantity > 0);
        if (items.length === 0) {
            message.info('ยังไม่ได้ระบุจำนวนฝากเพิ่มสำหรับสินค้าใด ปิดหน้าต่างนี้ได้เลย (รอบบิลปิดสำเร็จแล้ว)');
            return;
        }
        for (const r of items) {
            if (!Number.isInteger(r.addQuantity)) {
                message.error(`จำนวนฝากเพิ่มของ "${r.product_name}" ต้องเป็นจำนวนเต็ม (ไม่รองรับทศนิยม)`);
                return;
            }
            if (!Number.isFinite(r.sellingPrice) || Number(r.sellingPrice) < 0) {
                message.error(`ราคาขายของ "${r.product_name}" ไม่ถูกต้อง`);
                return;
            }
            if (!Number.isFinite(r.ownerCost) || Number(r.ownerCost) < 0) {
                message.error(`ต้นทุนผู้ฝากของ "${r.product_name}" ไม่ถูกต้อง`);
                return;
            }
        }

        setSaving(true);
        try {
            await onSaveBulk({
                expected_period_id: context.newPeriod.id,
                idempotency_key: idempotencyKey,
                items: items.map(r => ({
                    product_id: r.product_id,
                    add_quantity: Number(r.addQuantity),
                    selling_price_per_unit: Number(r.sellingPrice || 0),
                    owner_cost_per_unit: Number(r.ownerCost || 0)
                }))
            });
            message.success('ฝากเพิ่มรอบใหม่สำเร็จ');
            onClose();
        } catch (error: any) {
            const msg: string = error?.message || 'บันทึกฝากเพิ่มรอบใหม่ไม่สำเร็จ';
            if (msg.includes('STALE_PERIOD') || msg.includes('ALREADY_CLOSED')) {
                message.error('รอบบิลนี้มีการเปลี่ยนแปลงแล้ว กรุณารีเฟรชข้อมูลก่อนดำเนินการอีกครั้ง');
                onClose();
            } else {
                message.error(msg);
            }
        } finally {
            setSaving(false);
        }
    };

    const availableProductOptions = products
        .filter(p => !rows.some(r => r.product_id === p.id))
        .map(p => ({ value: p.id, label: `[${p.product_code || '-'}] ${p.product_name}` }));

    return (
        <Modal
            className="consignment-new-period-modal consignment-add-stock-modal"
            open={open}
            title={`📦 ฝากเพิ่มรอบใหม่ — รอบบิลที่ ${context.newPeriod.period_no}`}
            okText="บันทึกทั้งหมด"
            cancelText="ข้าม / ยังไม่ฝากเพิ่ม"
            confirmLoading={saving}
            onOk={handleSaveAll}
            onCancel={saving ? undefined : onClose}
            okButtonProps={{ disabled: saving, 'data-testid': 'btn-save-new-period-bulk' } as any}
            cancelButtonProps={{ disabled: saving, 'data-testid': 'btn-skip-new-period-bulk' } as any}
            destroyOnClose
            width={720}
        >
            <Space direction="vertical" size={14} style={{ width: '100%' }}>
                <Alert
                    type="info"
                    showIcon
                    message={`รอบบิลที่ ${context.newPeriod.period_no} ของ ${context.documentNo} ${context.vendorName ? `(${context.vendorName})` : ''} เปิดแล้ว`}
                    description="สต๊อกที่ยกมาจากรอบก่อนแสดงไว้ด้านล่างเพื่อการอ้างอิงเท่านั้น ไม่ต้องกรอกซ้ำ กรอกเฉพาะจำนวนที่ฝากเพิ่มใหม่จริงในรอบนี้"
                />

                {rows.length === 0 && (
                    <Empty description="ไม่มีสินค้ายกมาจากรอบก่อน" />
                )}

                {rows.map(row => (
                    <div className="consignment-bulk-row" key={row.product_id}>
                        <div className="consignment-bulk-row-header">
                            <div>
                                <div className="consignment-bulk-row-name">{row.product_name || `สินค้า #${row.product_id}`}</div>
                                <div className="consignment-bulk-row-code">รหัส: {row.product_code || '-'}</div>
                            </div>
                            {row.isExtra && (
                                <button
                                    type="button"
                                    className="consignment-bulk-row-remove"
                                    onClick={() => handleRemoveExtraRow(row.product_id)}
                                    data-testid={`btn-remove-extra-${row.product_id}`}
                                    aria-label="เอาสินค้านี้ออก"
                                >
                                    ✕
                                </button>
                            )}
                        </div>

                        {!row.isExtra && (
                            <div className="consignment-bulk-row-carry" data-testid={`carry-${row.product_id}`}>
                                ยกมาจากรอบก่อน: <strong>{row.carryTotal}</strong> {row.product_unit_name || 'ชิ้น'}
                                {row.carryBatches.length > 1 && (
                                    <span className="consignment-bulk-row-carry-detail">
                                        {' '}({row.carryBatches.map(b => `#${b.batch_no} ${b.quantity_received}@${Number(b.owner_cost_per_unit).toFixed(2)}`).join(', ')})
                                    </span>
                                )}
                            </div>
                        )}

                        <div className="consignment-bulk-row-fields">
                            <label>
                                <span className="consignment-add-stock-field-label">ฝากเพิ่มรอบนี้</span>
                                <InputNumber
                                    precision={0}
                                    placeholder="0"
                                    value={row.addQuantity}
                                    onChange={value => handleQuantityChange(row.product_id, value === null ? undefined : Number(value))}
                                    style={{ width: '100%' }}
                                    addonAfter={row.product_unit_name || 'ชิ้น'}
                                    data-testid={`input-bulk-qty-${row.product_id}`}
                                />
                            </label>
                            <label>
                                <span className="consignment-add-stock-field-label">ราคาขายต่อหน่วย</span>
                                <InputNumber
                                    min={0}
                                    precision={2}
                                    value={row.sellingPrice}
                                    onChange={value => handlePriceChange(row.product_id, 'sellingPrice', value === null ? undefined : Number(value))}
                                    addonBefore="฿"
                                    style={{ width: '100%' }}
                                    data-testid={`input-bulk-price-${row.product_id}`}
                                />
                            </label>
                            <label>
                                <span className="consignment-add-stock-field-label">ต้นทุนผู้ฝากต่อหน่วย</span>
                                <InputNumber
                                    min={0}
                                    precision={2}
                                    value={row.ownerCost}
                                    onChange={value => handlePriceChange(row.product_id, 'ownerCost', value === null ? undefined : Number(value))}
                                    addonBefore="฿"
                                    style={{ width: '100%' }}
                                    data-testid={`input-bulk-cost-${row.product_id}`}
                                />
                            </label>
                        </div>
                    </div>
                ))}

                <div className="consignment-bulk-add-extra">
                    <Select
                        showSearch
                        allowClear
                        placeholder="+ เพิ่มสินค้าอื่น"
                        optionFilterProp="label"
                        value={extraProductId}
                        options={availableProductOptions}
                        onChange={(value) => value !== undefined && handleAddExtraProduct(Number(value))}
                        style={{ width: '100%' }}
                        data-testid="select-add-extra-product"
                    />
                </div>
            </Space>
        </Modal>
    );
};

export default ConsignmentNewPeriodAddStockModal;
